/**
 * Resolves the trusted cross-domain embedding origin (e.g. `"https://refined.site"`) when this
 * Confluence page is embedded cross-domain, or `undefined` otherwise (so a truthy result doubles
 * as the "embedded cross-domain" signal). This is about the outer origin framing the page — not
 * Atlassian's Custom Domains feature. The only source is `window.__SSR_EMBEDDING_ORIGIN__` —
 * injected by Bifrost SSR from the value FrameAncestorsResolver derives from the verified `Referer`
 * and appends to Confluence's own `frame-ancestors` CSP — so the Forge macro's CSP can never be
 * looser and we avoid unreliable client APIs. Returns `undefined` when not SSR'd with a verified
 * cross-domain origin or when the origin is same-origin with this page.
 */
export const getCrossDomainEmbeddingOrigin = (): string | undefined => {
	// No `typeof window` guard: in Confluence bundles `window` is replaced at bundle time and always
	// exists, so it is not a valid SSR signal (see performance/rules/ssr.md). During SSR the global
	// below is simply absent, which the `typeof origin !== 'string'` check already handles.
	const origin = (window as unknown as { __SSR_EMBEDDING_ORIGIN__?: unknown })
		.__SSR_EMBEDDING_ORIGIN__;

	if (typeof origin !== 'string' || !origin) {
		return undefined;
	}

	// Must be a genuinely different (outer) origin — never echo this frame's own origin.
	return origin !== window.location.origin ? origin : undefined;
};
